import type { MaybeRefOrGetter } from 'vue';
import { computed, ref, toValue } from 'vue';
import { useTruncation } from './use-truncation';

/** Wraps and scrolls a long value inside the tooltip instead of truncating it again. */
export const truncatedTextTooltipUi = {
	content: 'h-auto max-w-100 max-h-60 items-start overflow-auto select-text',
	text: 'w-full min-w-0 overflow-visible text-clip whitespace-pre-wrap break-words',
};

interface TruncationTooltipOptions {
	/** Opens the tooltip even when `target` fits, for a tooltip that carries a notice instead of the text. */
	showWhenFits?: MaybeRefOrGetter<boolean>;
}

/**
 * Bindings for a controlled `DTooltip` that reveals `target`'s full text, opening only while `target` is cut off.
 *
 * Measures when the tooltip asks to open, so the width is never stale and text nobody lingers on costs no layout reads.
 * Bind it to a tooltip around the whole trigger: keyboard focus then opens it without a tab stop inside the trigger.
 */
export function useTruncationTooltip(
	target: MaybeRefOrGetter<HTMLElement | null | undefined>,
	{ showWhenFits = false }: TruncationTooltipOptions = {},
) {
	const { measure } = useTruncation(target);
	const open = ref(false);

	function updateOpen(value: boolean) {
		open.value = value && (toValue(showWhenFits) || measure());
	}

	return computed(() => ({
		'open': open.value,
		'ui': truncatedTextTooltipUi,
		'onUpdate:open': updateOpen,
	}));
}
